import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CLINICAL_PATHS } from '../../../helpers/clinical_account';
import { useReportableEventCounts } from '../reportable_events/counts_context';

import ClinicalPage from '../page';
import Context from '../../../utils/context';
import NavBadge from './nav_badge';
import ProgramInsights from '../program_insights';
import ReportableEventCountsProvider from '../reportable_events/counts_context';
import ReportableEvents from '../reportable_events';
import SimpleSlidingMenu from '../../ui_kit/utilities/simple_sliding_menu';

import styles from './index.module.css';

const PAGES = [
  {
    text: 'Program Insights',
    icon: '◇',
    url: CLINICAL_PATHS.programInsights,
    eyebrow: 'Program metrics',
    subtitle: 'Adoption, engagement, and satisfaction across the PatientPartner mentorship program.',
  },
  {
    text: 'Study Insights',
    icon: '▤',
    url: CLINICAL_PATHS.studyInsights,
    eyebrow: 'Study metrics',
    subtitle: 'Study-level rollout, sentiment, and in-study survey results.',
  },
  {
    text: 'Retention Insights',
    icon: '◈',
    url: CLINICAL_PATHS.retentionInsights,
    eyebrow: 'Retention metrics',
    subtitle: 'Participation, engagement, and dropout impact attributable to mentorship.',
  },
  {
    text: 'Reportable Events',
    icon: '⚑',
    url: CLINICAL_PATHS.reportableEvents,
    showsEventCounts: true,
  },
];

// Reportable Events and Program Insights bring their own page header and content, so only the
// remaining insights placeholders render through ClinicalPage.
const renderPage = (page) => {
  if (page.url === CLINICAL_PATHS.reportableEvents) {
    return <ReportableEvents />;
  }

  if (page.url === CLINICAL_PATHS.programInsights) {
    return <ProgramInsights />;
  }

  return <ClinicalPage eyebrow={page.eyebrow} subtitle={page.subtitle} title={page.text} />;
};

const ClinicalRoutes = () => (
  <Switch>
    {PAGES.map((page) => (
      <Route exact key={page.url} path={page.url}>
        {renderPage(page)}
      </Route>
    ))}
    <Route exact path={CLINICAL_PATHS.root}>
      <Redirect to={CLINICAL_PATHS.programInsights} />
    </Route>
  </Switch>
);

function ClinicalHomeLayout({ currentUser }) {
  const viewport = useContext(Context).viewport;
  const [, setViewportVersion] = useState(0);
  const isMobile = viewport.getIsMobile();
  const location = useLocation();
  const { outstandingCount } = useReportableEventCounts();
  const accountName = currentUser?.account?.name;

  useEffect(() => {
    const onResize = () => setViewportVersion((version) => version + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // idToScrollTo must be a valid DOM id: SimpleSlidingMenu uses it in a
  // `#id` querySelector, so a path like /clinical/program-insights would throw.
  const menuItems = PAGES.map(({ text, url, showsEventCounts }) => ({
    linkText: text,
    idToScrollTo: `clinical-menu-${url.split('/').pop()}`,
    href: url,
    ariaCurrent: location.pathname === url ? 'page' : undefined,
    badge: showsEventCounts ? <NavBadge outstandingCount={outstandingCount} /> : null,
  }));

  let defaultSelectedMenuItem = menuItems[0];
  menuItems.forEach((item) => {
    if (item.href === location.pathname) {
      defaultSelectedMenuItem = item;
    }
  });
  const [selectedMenuItem, setSelectedMenuItem] = useState(defaultSelectedMenuItem);
  const shellRef = useRef(null);
  const sidebarRef = useRef(null);
  const mainRef = useRef(null);

  useEffect(() => {
    setSelectedMenuItem(defaultSelectedMenuItem);
  }, [location.pathname]);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  // The site header and admin session bar sit above this layout. Pin the shell to the
  // remaining viewport and keep the window itself from scrolling, so only the graph pane moves.
  useLayoutEffect(() => {
    if (isMobile) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const updateOffset = () => {
      const header = document.getElementById('hdr-content');
      const headerHeight = header ? header.offsetHeight : 0;
      const bannerHeight =
        Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--impersonation-banner-height')
        ) || 0;
      const top = headerHeight + bannerHeight;
      const height = Math.max(window.innerHeight - top, 0);

      document.documentElement.style.setProperty('--clinical-header-offset', `${headerHeight}px`);

      // A calc() height on this fixed flex item collapses to the nav's content height.
      // Pin the used size in pixels so the sidebar reaches the bottom of the viewport.
      if (shellRef.current) {
        shellRef.current.style.top = `${top}px`;
        shellRef.current.style.height = `${height}px`;
        shellRef.current.style.minHeight = `${height}px`;
      }
      if (sidebarRef.current) {
        sidebarRef.current.style.height = `${height}px`;
        sidebarRef.current.style.minHeight = `${height}px`;
      }
    };

    updateOffset();
    window.addEventListener('resize', updateOffset);
    const header = document.getElementById('hdr-content');
    const observer = new ResizeObserver(updateOffset);
    if (header) observer.observe(header);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
      document.documentElement.style.removeProperty('--clinical-header-offset');
      if (shellRef.current) {
        shellRef.current.style.top = '';
        shellRef.current.style.height = '';
        shellRef.current.style.minHeight = '';
      }
      if (sidebarRef.current) {
        sidebarRef.current.style.height = '';
        sidebarRef.current.style.minHeight = '';
      }
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile]);

  if (isMobile) {
    return (
      <div className={styles.shell_mobile}>
        <div className={styles.mobile_menu}>
          <SimpleSlidingMenu menuItems={menuItems} setMarker={selectedMenuItem} setMarkerItem={setSelectedMenuItem} />
        </div>
        <ClinicalRoutes />
      </div>
    );
  }

  return (
    <div className={styles.shell} ref={shellRef}>
      <aside className={styles.sidebar} ref={sidebarRef}>
        <p className={styles.sidebar_label}>Clinical Analytics</p>
        <nav aria-label='Clinical dashboard' className={styles.sidebar_nav}>
          {PAGES.map((page) => {
            const isSelected = location.pathname === page.url;

            return (
              <Link
                key={page.url}
                aria-current={isSelected ? 'page' : undefined}
                className={`${styles.nav_link} ${isSelected ? styles.selected : ''}`}
                to={page.url}
              >
                <span aria-hidden='true' className={styles.nav_icon}>
                  {page.icon}
                </span>
                <span className={styles.nav_text}>{page.text}</span>
                {page.showsEventCounts && <NavBadge outstandingCount={outstandingCount} />}
              </Link>
            );
          })}
        </nav>
        {accountName && (
          <div className={styles.study_card}>
            <p className={styles.study_card_label}>Study</p>
            <p className={styles.study_card_name}>{accountName}</p>
          </div>
        )}
      </aside>
      <main className={styles.main} ref={mainRef}>
        <ClinicalRoutes />
      </main>
    </div>
  );
}

export default function ClinicalHome({ currentUser }) {
  return (
    <ReportableEventCountsProvider>
      <ClinicalHomeLayout currentUser={currentUser} />
    </ReportableEventCountsProvider>
  );
}
